import { IFieldDef } from "@components/smart/FieldInfo";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    PrSalaryComponentEntity,
    PrSalaryComponentTemporalEntity,
    PrSalaryComponentTypeEntity
} from "@odata/GeneratedEntityTypes";
import { PrRegularSalaryTypeCode, PrSalaryComponentTypeCode } from "@odata/GeneratedEnums";
import { transformToODataString } from "@odata/OData.utils";
import {
    getTableTemporalAdditionalProperties,
    getTemporalAdditionalProperties,
    getTemporalDataColumns,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import { getPayrollCurrencyTableFormatter } from "@pages/payroll/payroll.utils";
import {
    getFilterForTabId,
    getSalaryComponentColumns,
    getSalaryComponentFieldDefinitions,
    getSalaryEnumNamespaceNames,
    SalaryComponentTab,
    SalaryComponentVariant
} from "@pages/payroll/salaryComponent/SalaryComponent.utils";
import i18next from "i18next";

import { Sort, ValueType } from "../../../enums";
import { Model } from "../../../model/Model";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef, ITableDefTabData, SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import SalaryComponentFormView from "./SalaryComponentFormView";

export const getDefinitionsFactory = (inEditableWindow?: boolean, selectedIds?: number[], isCompensation?: boolean, isDpp?: boolean, isDelayedIncome?: boolean): IGetDefinition => {
    const getDefinitions: IGetDefinition = (): IDefinition => {

        const preventStoreVariant = inEditableWindow;

        const filters: string[] = [];
        if (selectedIds?.length) {
            filters.push(`not(${PrSalaryComponentEntity.Id} in (${transformToODataString(selectedIds, ValueType.Number)}))`);
        }

        if (inEditableWindow) {
            filters.push(getFilterForTabId(SalaryComponentTab.Active));
        }

        if (isDelayedIncome) {
            filters.push(`(${PrSalaryComponentEntity.TypeCode} ne '${PrSalaryComponentTypeCode.Regular}')`);
        }
        
        if (isCompensation) {
            filters.push(`(${PrSalaryComponentEntity.TypeCode} ne '${PrSalaryComponentTypeCode.Regular}'
             or ${PrSalaryComponentEntity.RegularSalaryTypeCode} eq '${PrRegularSalaryTypeCode.Monthly}')`);
        }

        if (isDpp) {
            filters.push(`${PrSalaryComponentEntity.RegularSalaryTypeCode} ne '${PrRegularSalaryTypeCode.Monthly}'`);
        }

        const defaultTabs: ITableDefTabData[] = [
            { id: SalaryComponentTab.Active, title: i18next.t("SalaryComponent:Tabs.Active") },
            { id: SalaryComponentTab.Inactive, title: i18next.t("SalaryComponent:Tabs.Inactive") },
            { id: SECONDARY_FILTERS_ALL, title: i18next.t("SalaryComponent:Tabs.All") }
        ];
        const tabs: ITableDefTabData[] = inEditableWindow ? null : defaultTabs;

        const tableAdditionalProperties: IFieldDef[] = [];

        if(inEditableWindow) {
            const cols = getTemporalDataColumns(getSalaryComponentColumns(SalaryComponentVariant.Employment));

            cols.forEach(col => {
                tableAdditionalProperties.push({ id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, col) });
                tableAdditionalProperties.push({ id: createPath(PrSalaryComponentEntity.TemporalPropertyBag, col) });
            });
            tableAdditionalProperties.push(...[
                { id: PrSalaryComponentEntity.TypeCode },
                { id: PrSalaryComponentEntity.RegularSalaryTypeCode },
            ]);
        } else {
            tableAdditionalProperties.push(...getTableTemporalAdditionalProperties(PrSalaryComponentEntity));
        }

        const table: ISplitPageTableDef = {
            id: `${EntityTypeName.PrSalaryComponent}Table`,
            preventStoreVariant,
            filterBarDef: [{
                ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
                defaultFilters: [
                    createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name),
                    PrSalaryComponentEntity.Type,
                    createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)
                ],
                filterDefinition: {
                    [PrSalaryComponentEntity.Type]: {
                        fieldSettings: {
                            displayName: PrSalaryComponentTypeEntity.Name
                        }
                    },
                    [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name)]: {},
                    [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)]: {
                        formatter: getPayrollCurrencyTableFormatter()
                    }
                },
                isValueHelp: true
            }],
            filter: filters.join(" AND "),
            initialSortBy: [{
                id: PrSalaryComponentEntity.Type,
                sort: Sort.Asc
            }, {
                id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name),
                sort: Sort.Asc
            }],
            columns: [
                createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name),
                PrSalaryComponentEntity.Type,
                createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)
            ],
            additionalProperties: tableAdditionalProperties,
            columnDefinition: {
                [PrSalaryComponentEntity.Type]: {
                    fieldSettings: {
                        displayName: PrSalaryComponentTypeEntity.Name
                    }
                },
                [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name)]: {},
                [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)]: {
                    formatter: getPayrollCurrencyTableFormatter()
                }
            },
            title: i18next.t("SalaryComponent:Title"),
            tabs,
            tabsSettings: {
                customFilter: getFilterForTabId
            }
        };
        const summary: ISummaryItem[] = [];

        const form: IFormDef = {
            id: `${EntityTypeName.PrSalaryComponent}Form`,
            translationFiles: getDefinitions.translationFiles,
            getItemBreadCrumbText: (storage: Model) =>
                    getItemBreadCrumbsText(storage, i18next.t("SalaryComponent:New"), i18next.t("SalaryComponent:FormTitle")),
            isDeletable: true,
            formControl: SalaryComponentFormView,
            summary,
            additionalProperties: [
                ...getTemporalAdditionalProperties(PrSalaryComponentTemporalEntity, PrSalaryComponentEntity)
            ],
            fieldDefinition: {
                ...TemporalValidityFieldsDef,
                ...getSalaryComponentFieldDefinitions(),
            },
            groups: [
                {
                    id: "main",
                    rows: [
                        [
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name) },
                            { id: PrSalaryComponentEntity.Type },
                            { id: PrSalaryComponentEntity.RegularSalaryType },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.IsBasicSalary) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Percentage) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.FixedBonusTaxImpact) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.FixedBonusPayOutImpact) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.FixedBonusDeductionImpact) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount) }
                        ]

                    ]
                }
            ]
        };
        return {
            entitySet: EntitySetName.PrSalaryComponents,
            table,
            form
        };
    };

    getDefinitions.translationFiles = ["SalaryComponent", "Common", ...getSalaryEnumNamespaceNames()];

    return getDefinitions;
};

export const getDefinitions = getDefinitionsFactory();

setDefByEntityType(EntityTypeName.PrSalaryComponent, getDefinitions);